import { GameState } from './GameState';
import { saveSystem } from './SaveSystem';
import { EventBus, GameEvents } from '../core/EventBus';
import { GameData } from '../data/GameData';
import { AttributeKey, AchievementDef } from '../data/types';
import { AwardId } from '../data/tables/Awards';

/** 属性类成就的判定域 = 七维可训练属性（⛔ 含 reputation 会让「六边形战士」被口碑晋升线绑架） */
const ACHIEVE_ATTR_KEYS = GameData.ATTRIBUTE_KEYS.filter(k => k !== 'reputation') as AttributeKey[];

/**
 * 成就系统（单例）
 * 每月结算（GameLoop.finishMonth）统一判定一次全部成就条件 + endGame 时补判一次（幂等兜终局达标）。
 * 判定全部纯读现有数据字段（filmography 快照 / awardHistory / fans / totalPosts 等计数器 /
 * pathCourseCounts / relationships / guestedBy），无新落盘。
 * 达成即发放成就点并写入 MetaData（已达成不重复给），周目间累积。
 * 旧版 ACH_001~022 已整批作废：读取 meta 时按当前表白名单过滤，失效 ID 自动剔除（测试档脏数自愈）。
 */
export class AchievementSystem {
    private static instance: AchievementSystem;

    private constructor() { }

    static getInstance(): AchievementSystem {
        if (!this.instance) {
            this.instance = new AchievementSystem();
        }
        return this.instance;
    }

    /** 当前是否已解锁某成就（跨周目；表中已不存在的旧 ID 视为未解锁） */
    isUnlocked(id: string): boolean {
        return GameData.ACHIEVEMENT_MAP[id] !== undefined
            && saveSystem.loadMeta().unlockedAchievements.includes(id);
    }

    /** 本月结算判定：返回本次新解锁的成就 ID 列表（无则空数组） */
    checkMonthly(): string[] {
        const state = GameState.getInstance();
        const meta = saveSystem.loadMeta();
        // 白名单过滤：剔除已作废的旧成就 ID（ACH_001~022），顺带写回清理
        const kept = meta.unlockedAchievements.filter(id => GameData.ACHIEVEMENT_MAP[id] !== undefined);
        const unlocked = new Set(kept);
        const newly: string[] = [];

        for (const def of GameData.ACHIEVEMENTS) {
            if (unlocked.has(def.id)) continue; // 已达成不重复给
            if (this.evaluate(state, def.conditionType, def.conditionValue, def.conditionAttr)) {
                unlocked.add(def.id);
                meta.achievementPoints += def.points;
                newly.push(def.id);
                // 本周目记账（结局批 2026-09-30）：结算页「周目结算」按 runAchievements 全量展示，
                // ⛔ 只靠 endGame 补判那一次返回值（平时月度解锁会全部漏掉）；旧档缺省不记账不升版
                state.player.runAchievements = [...(state.player.runAchievements ?? []), def.id];
            }
        }

        if (newly.length > 0 || kept.length !== meta.unlockedAchievements.length) {
            meta.unlockedAchievements = [...unlocked];
            saveSystem.saveMeta(meta);
            for (const id of newly) {
                EventBus.emit(GameEvents.ACHIEVEMENT_UNLOCKED, id);
            }
        }
        return newly;
    }

    /** 判定单个成就条件是否满足 */
    private evaluate(
        state: GameState,
        type: AchievementDef['conditionType'],
        value: number | string,
        attr?: AchievementDef['conditionAttr'],
    ): boolean {
        const player = state.player;
        switch (type) {
            case 'rank':
                return player.rank >= (value as number);
            case 'attribute':
                return state.getAttribute(attr as AttributeKey) >= (value as number);
            case 'anyAttr':
                return Math.max(...ACHIEVE_ATTR_KEYS.map(k => state.getAttribute(k))) >= (value as number);
            case 'allAttr':
                return ACHIEVE_ATTR_KEYS.every(k => state.getAttribute(k) >= (value as number));
            case 'money':
                return player.totalEarnedMoney >= (value as number);
            case 'shoots':
                return player.totalShotsCompleted >= (value as number);
            case 'works':
                return (player.filmography?.length ?? 0) >= (value as number);
            case 'score': {
                const scores = (player.filmography ?? []).map(f => f.score);
                return scores.length > 0 && Math.max(...scores) >= (value as number);
            }
            case 'avgScore': {
                // 复合值「均分,最少部数」：均分门槛配合部数下限，防一两部高分就达成
                const [minAvg, minCount] = String(value).split(',').map(Number);
                const films = player.filmography ?? [];
                if (films.length < minCount) return false;
                return films.reduce((s, f) => s + f.score, 0) / films.length >= minAvg;
            }
            case 'awards': {
                const won = player.awardHistory?.filter(r => r.playerWon) ?? [];
                if (attr === 'both') {
                    // 双料：金枢奖与星汉奖各≥1（scripted 开场剧情届 playerWon 恒 false，天然不计）
                    return won.some(r => r.awardId === ('jingshu' satisfies AwardId))
                        && won.some(r => r.awardId === ('xinghan' satisfies AwardId));
                }
                return won.length >= (value as number);
            }
            case 'nominations':
                return (player.awardHistory?.filter(r => r.playerNominated).length ?? 0) >= (value as number);
            case 'fans':
                return player.fans >= (value as number);
            case 'likes':
                return (player.totalLikes ?? 0) >= (value as number);
            case 'courses':
                return Object.values(player.pathCourseCounts).reduce((a, b) => a + b, 0) >= (value as number);
            case 'friends':
                // 挚友档 = 好感 ≥80（与周目结算「挚友 X 人」同口径）
                return Object.values(player.relationships).filter(f => f >= 80).length >= (value as number);
            case 'known':
                return Object.values(player.relationships).filter(f => f >= 0).length >= (value as number);
            case 'guestOut': {
                // 请好友客串总人次：guestedBy{scriptId: npcId[]} 拍扁计数（成功请托才记账，一好友一剧一次）
                const ledger = player.guestedBy ?? {};
                return Object.values(ledger).reduce((a, list) => a + list.length, 0) >= (value as number);
            }
            case 'guestIn':
                return (player.totalGuestRides ?? 0) >= (value as number);
            case 'rankLowRep': {
                // 复合值「咖位下限,口碑上限」：黑红路线（Lv.7 无口碑晋升门槛，路线成立）
                const [minRank, maxRep] = String(value).split(',').map(Number);
                return player.rank >= minRank && state.getAttribute('reputation') <= maxRep;
            }
            case 'rankNoPosts':
                return player.rank >= (value as number) && (player.totalPosts ?? 0) === 0;
            case 'shootsNoAwards':
                return player.totalShotsCompleted >= (value as number)
                    && !(player.awardHistory?.some(r => r.playerWon) ?? false);
            case 'event':
                return state.hasFlag(value as string);
            case 'tag': {
                // 组合标签：conditionValue = 'TAG_006,TAG_007'，全部拥有才达成
                const ids = String(value).split(',').map(s => s.trim());
                return ids.length > 0 && ids.every(id => player.tags.includes(id));
            }
            default:
                return false;
        }
    }
}

/** 导出单例 */
export const achievementSystem = AchievementSystem.getInstance();
